import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function HotelTariffPlans() {
  const [tariffs, setTariffs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    category_id: "",
    plan_type: "EP",
    season: "Regular",
    weekday_price: 0,
    weekend_price: 0,
    extra_bed_charge: 0,
    extra_person_charge: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tariffsRes, categoriesRes] = await Promise.all([
        api.get("/hotel/tariffs"),
        api.get("/hotel/categories"),
      ]);
      setTariffs(tariffsRes.data.tariffs || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tariff plans",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tariff = null) => {
    if (tariff) {
      setEditingTariff(tariff);
      setFormData({
        category_id: tariff.category_id,
        plan_type: tariff.plan_type,
        season: tariff.season,
        weekday_price: tariff.weekday_price,
        weekend_price: tariff.weekend_price,
        extra_bed_charge: tariff.extra_bed_charge,
        extra_person_charge: tariff.extra_person_charge,
      });
    } else {
      setEditingTariff(null);
      setFormData({
        category_id: "",
        plan_type: "EP",
        season: "Regular",
        weekday_price: 0,
        weekend_price: 0,
        extra_bed_charge: 0,
        extra_person_charge: 0,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTariff(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTariff) {
        await api.put(`/hotel/tariffs/${editingTariff.id}`, formData);
        toast({
          title: "Success",
          description: "Tariff plan updated successfully",
        });
      } else {
        await api.post("/hotel/tariffs", formData);
        toast({
          title: "Success",
          description: "Tariff plan created successfully",
        });
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save tariff plan",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this tariff plan?")) {
      return;
    }

    try {
      await api.delete(`/hotel/tariffs/${id}`);
      toast({
        title: "Success",
        description: "Tariff plan deleted successfully",
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete tariff plan",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin h-12 w-12 text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
            Tariff Plans
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Manage room pricing and meal plans
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="gap-2">
          <Plus size={16} />
          Add Tariff Plan
        </Button>
      </div>

      {/* Tariffs Table */}
      <Card className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Plan Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Season</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Weekday</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Weekend</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Extra Bed</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tariffs.map((tariff) => (
              <tr key={tariff.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">{tariff.category_name}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                    {tariff.plan_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{tariff.season}</td>
                <td className="px-4 py-3 text-sm text-right">₹{tariff.weekday_price}</td>
                <td className="px-4 py-3 text-sm text-right">₹{tariff.weekend_price}</td>
                <td className="px-4 py-3 text-sm text-right">₹{tariff.extra_bed_charge}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenModal(tariff)}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(tariff.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTariff ? "Edit Tariff Plan" : "Add New Tariff Plan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="category_id">Room Category *</Label>
              <Select
                value={formData.category_id.toString()}
                onValueChange={(val) =>
                  setFormData({ ...formData, category_id: parseInt(val) })
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plan_type">Plan Type *</Label>
                <Select
                  value={formData.plan_type}
                  onValueChange={(val) =>
                    setFormData({ ...formData, plan_type: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EP">EP (Room Only)</SelectItem>
                    <SelectItem value="CP">CP (Bed + Breakfast)</SelectItem>
                    <SelectItem value="MAP">MAP (Half Board)</SelectItem>
                    <SelectItem value="AP">AP (Full Board)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="season">Season *</Label>
                <Select
                  value={formData.season}
                  onValueChange={(val) =>
                    setFormData({ ...formData, season: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Peak">Peak</SelectItem>
                    <SelectItem value="Off-Season">Off-Season</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="weekday_price">Weekday Price *</Label>
                <Input
                  id="weekday_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.weekday_price}
                  onChange={(e) =>
                    setFormData({ ...formData, weekday_price: parseFloat(e.target.value) })
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="weekend_price">Weekend Price *</Label>
                <Input
                  id="weekend_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.weekend_price}
                  onChange={(e) =>
                    setFormData({ ...formData, weekend_price: parseFloat(e.target.value) })
                  }
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="extra_bed_charge">Extra Bed Charge</Label>
                <Input
                  id="extra_bed_charge"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.extra_bed_charge}
                  onChange={(e) =>
                    setFormData({ ...formData, extra_bed_charge: parseFloat(e.target.value) })
                  }
                />
              </div>

              <div>
                <Label htmlFor="extra_person_charge">Extra Person Charge</Label>
                <Input
                  id="extra_person_charge"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.extra_person_charge}
                  onChange={(e) =>
                    setFormData({ ...formData, extra_person_charge: parseFloat(e.target.value) })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit">
                {editingTariff ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
